import type {
  BarNestedValue,
  BarPlotData,
  BarStack,
  PlotDatum,
  PlotScale,
  ScatterPlotDatum,
} from "@/components/VivoPlots/functions/plotTableView.types";
import { isValueValid } from "@/components/VivoPlots/functions/utils/valueUtils";
import type {
  PlotTableColumn,
  PlotTableColumnRole,
  PlotTableView,
} from "@/components/VivoPlots/types";

const activeMapping = (scale?: PlotScale) =>
  scale?.dataMapping && scale.dataMapping !== "none" ? scale.dataMapping : null;

const optionalColumn = (
  key: string,
  field: string | null,
  role: PlotTableColumnRole,
): PlotTableColumn[] => (field ? [{ key, field, role }] : []);

const isBarStack = (value: BarNestedValue): value is BarStack =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const effectiveDomain = (scale?: PlotScale) =>
  Array.isArray(scale?.domain)
    ? scale.domain
    : Array.isArray(scale?.predefinedDomain)
      ? scale.predefinedDomain
      : undefined;

const domainIncludesValue = (domain: unknown[] | undefined, value: unknown) =>
  Array.isArray(domain) &&
  domain.some((item) => String(item) === String(value));

export const columnTitle = (
  column: PlotTableColumn,
  columnsTitleCreator?: (column: string) => string,
) => {
  if (column.role === "count") return "Count";
  if (column.role === "percentage") return "Subject Percentage";
  return columnsTitleCreator ? columnsTitleCreator(column.field) : column.field;
};

export const buildScatterTableView = (
  points: ScatterPlotDatum[],
  scales: PlotScale[],
  pairFields: string[] = [],
): PlotTableView => {
  const xField = activeMapping(
    scales.find((scale) => scale.position === "bottom"),
  );
  const yField = activeMapping(
    scales.find((scale) => scale.position === "left"),
  );
  const colorField = activeMapping(
    scales.find((scale) => scale.type === "fill"),
  );

  const columns: PlotTableColumn[] = [
    ...pairFields.map<PlotTableColumn>((field) => ({
      key: `pair:${field}`,
      field,
      role: "pair",
    })),
    ...optionalColumn("color", colorField, "color"),
    ...optionalColumn("x", xField, "x"),
    ...optionalColumn("y", yField, "y"),
  ];

  const rows = (points ?? []).map((point) => {
    const row: Record<string, unknown> = {};

    for (const field of pairFields) {
      row[`pair:${field}`] = point[field];
    }

    if (colorField) {
      row.color = point[colorField];
    }

    row.x = point.xLabel ?? point.x;
    row.y = point.yLabel ?? point.y;
    return row;
  });

  return { columns, rows };
};

export const buildSwarmTableView = (
  data: PlotDatum[],
  scales: PlotScale[],
): PlotTableView => {
  const xField = activeMapping(
    scales.find((scale) => scale.type === "categorical"),
  );
  const yField = activeMapping(
    scales.find((scale) => scale.type === "continuous"),
  );
  const subgroupScale = scales.find((scale) => scale.type === "subgroup");
  const groupField = activeMapping(subgroupScale);
  const colorField = activeMapping(
    scales.find((scale) => scale.type === "fill"),
  );

  const dimensions: PlotTableColumn[] = [
    ...optionalColumn("x", xField, "category"),
    ...optionalColumn("group", groupField, "group"),
    ...optionalColumn("color", colorField, "color"),
  ];

  const columns: PlotTableColumn[] = [
    ...dimensions,
    ...optionalColumn("y", yField, "y"),
  ];

  const groupDomain = effectiveDomain(subgroupScale);

  const rows = (data ?? [])
    .filter((datum) => yField && isValueValid(datum[yField]))
    .filter((datum) => {
      if (!groupField || !Array.isArray(groupDomain)) {
        return true;
      }

      return (
        !groupDomain.length ||
        domainIncludesValue(groupDomain, datum[groupField])
      );
    })
    .map((datum) => {
      const row: Record<string, unknown> = {};
      for (const dimension of dimensions) {
        row[dimension.key] = datum[dimension.field];
      }
      if (yField) {
        row.y = datum[yField];
      }
      return row;
    });

  return { columns, rows };
};

export const buildBarTableView = (
  plotData: BarPlotData,
  scales: PlotScale[],
): PlotTableView => {
  const categoryField = activeMapping(
    scales.find((scale) => scale.type === "categorical"),
  );
  const groupField = activeMapping(
    scales.find((scale) => scale.type === "subgroup"),
  );
  const colorField = activeMapping(
    scales.find((scale) => scale.type === "fill"),
  );
  const nestedData = plotData?.nestedData ?? [];
  const isPercentage = [...(plotData?.scales ?? []), ...scales].some(
    (scale) => scale.type === "total" && scale.showPercentage,
  );
  const subgroupScale = scales.find((scale) => scale.type === "subgroup");
  const fillScale = scales.find((scale) => scale.type === "fill");
  const subgroupDomain = effectiveDomain(subgroupScale);
  const fillDomain = effectiveDomain(fillScale);

  const dimensions: PlotTableColumn[] = [
    ...optionalColumn("category", categoryField, "category"),
    ...optionalColumn("group", groupField, "group"),
    ...optionalColumn("color", colorField, "color"),
  ];
  const valueColumn: PlotTableColumn = isPercentage
    ? { key: "percentage", field: "__percentage", role: "percentage" }
    : { key: "count", field: "__count", role: "count" };
  const columns = [...dimensions, valueColumn];
  const rows: Record<string, unknown>[] = [];

  for (const entry of nestedData) {
    const category = categoryField ? entry[categoryField] : undefined;

    for (const [subgroupValue, stacks] of Object.entries(entry)) {
      if (subgroupValue === categoryField || !isBarStack(stacks)) continue;
      if (
        Array.isArray(subgroupDomain) &&
        subgroupDomain.length > 0 &&
        !domainIncludesValue(subgroupDomain, subgroupValue)
      ) {
        continue;
      }

      for (const [stackValue, value] of Object.entries(stacks)) {
        if (
          Array.isArray(fillDomain) &&
          fillDomain.length > 0 &&
          !domainIncludesValue(fillDomain, stackValue)
        ) {
          continue;
        }

        const row: Record<string, unknown> = {};
        if (categoryField) row.category = category;
        if (groupField) row.group = subgroupValue;
        if (colorField) row.color = stackValue;
        row[valueColumn.key] = value;
        rows.push(row);
      }
    }
  }

  return { columns, rows };
};

export const EMPTY_TABLE_VIEW: PlotTableView = { columns: [], rows: [] };
